import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ic_ambulance, ic_date, ic_menu } from "../assets/layout";
import { pp_savani } from "../assets";
import { navItems, additionalItems } from "../lib";
import { cx } from "../utils";
import { FaPhoneAlt, FaUserCircle } from "react-icons/fa";
import NavItem from "./index";
import SpecialtiesDropdown from "../components/SpecialtiesDropdown";

function NavBar() {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAmbulanceDropdownOpen, setIsAmbulanceDropdownOpen] = useState(false);
  const [isSpecialtiesOpen, setIsSpecialtiesOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("");
  const [mobileTreatmentOpen, setMobileTreatmentOpen] = useState(false);

  const ambulanceRef = useRef(null);
  const sidebarRef = useRef(null);
  const overlayRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate(null);
  const location = useLocation();

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const navigateBookPage = () => {
    navigate("/book-appointment");
  };

  // Check if token is present in cookies
  const checkLoginStatus = () => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="));
    return !!token;
  };

  useEffect(() => {
    setIsLoggedIn(checkLoginStatus());
  });

  // Set active menu based on current path
  useEffect(() => {
    const currentPath = location.pathname;
    const activeItem = navItems.find(
      (item) => item.pathname && currentPath.startsWith(item.pathname)
    );
    setActiveMenu(activeItem ? activeItem.text : "");
  }, [location]);

  const toggleAmbulanceDropdown = () => {
    setIsAmbulanceDropdownOpen(!isAmbulanceDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        ambulanceRef.current &&
        !ambulanceRef.current.contains(event.target)
      ) {
        setIsAmbulanceDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const logoutHandler = () => {
    document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    setIsLoggedIn(false);
    navigate("/login");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        !overlayRef.current.contains(event.target)
      ) {
        setIsNavOpen(false);
      }

      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleMouseEnter = () => {
    setIsSpecialtiesOpen(true);
  };

  const handleMouseLeave = () => {
    setIsSpecialtiesOpen(false);
  };

  const toggleMobileTreatment = () => {
    setMobileTreatmentOpen(!mobileTreatmentOpen);
  };

  // Replace this useEffect in NavBar.js
  useEffect(() => {
    const currentPath = location.pathname;
    const activeItem = navItems.find((item) => {
      if (item.pathname === "/") {
        return currentPath === "/";
      }
      return item.pathname && currentPath.startsWith(item.pathname);
    });
    setActiveMenu(activeItem ? activeItem.text : "");
  }, [location]);

  return (
    <>
      {/* Desktop Nav - Only show on screens larger than 1024px */}
      <header className="xl:block hidden font-PoppinsRegular fixed top-0 left-0 right-0 bg-white z-[1001] shadow-sm">
        <div className="grid grid-cols-11 grid-rows-3 border-b border-[#D5D5D5]">
          <Link
            to={"/"}
            className="col-span-3 row-span-3 flex gap-3 px-3 items-center justify-evenly border-r border-[#D5D5D5] bg-white"
          >
            <img
              src={pp_savani}
              alt="pp_savani"
              width={120}
              height={120}
              className="2xl:w-96 xl:w-80 lg:w-80 md:w-72 w-auto h-auto"
            />
          </Link>

          {/* Top Navigation - adjusted col-start to 4 and col-span to 7 */}
          <div className="col-span-7 col-start-4 flex justify-between border-b border-[#D5D5D5] px-5 py-2 bg-white">
            <Link to={"/book-appointment"} className="flex items-center gap-2">
              <h1 className="font-poppins 2xl:text-sm text-xs text-primary font-semibold">
                Book Online
              </h1>
              <span className="text-secondary font-poppins font-semibold">
                →
              </span>
              <p className="text-secondary 2xl:text-sm text-xs font-PoppinsRegular font-semibold">
                You can request appointment in 24 hours
              </p>
            </Link>
            <div className="text-primary text-sm font-medium font-PoppinsRegular flex items-center gap-5">
              <Link to="/visitors">
                Visitor Guidelines{" "}
                <span className="text-secondary font-bold text-base">+</span>
              </Link>
              <Link to="/patient-process">
                Patients Process{" "}
                <span className="text-secondary font-bold text-base">+</span>
              </Link>
            </div>
          </div>

          {/* User/Ambulance Section - adjusted col-start to 11 */}
          <div className="col-start-11 flex gap-2 items-center border-b border-[#D5D5D5] py-2 z-[1002] bg-white relative">
            <div className="relative" ref={ambulanceRef}>
              <img
                src={ic_ambulance}
                alt="ic_ambulance"
                className="cursor-pointer"
                onClick={toggleAmbulanceDropdown}
              />

              {/* Dropdown for Ambulance Number */}
              <div
                className={`absolute right-0 mt-2 w-40 bg-white shadow-lg rounded-lg transition-all transform origin-top ${
                  isAmbulanceDropdownOpen
                    ? "opacity-100 scale-y-100"
                    : "opacity-0 scale-y-0"
                }`}
                style={{ transitionDuration: "300ms" }}
              >
                <div className="p-3 text-center">
                  <p className="text-lg font-bold text-primary">Ambulance</p>
                  <p className="text-xl font-semibold text-gray-700">
                    9925077200
                  </p>
                  <a
                    href="tel:9925077200"
                    className="flex mt-2 text-white bg-primary px-4 py-2 rounded-md items-center justify-center gap-2"
                  >
                    <FaPhoneAlt /> Call Now
                  </a>
                </div>
              </div>
            </div>
            <div className="relative" ref={dropdownRef}>
              {/* User Icon */}
              <div onClick={toggleDropdown} className="cursor-pointer">
                <FaUserCircle
                  className="text-primary"
                  size={30}
                  title="Login Icon"
                />
              </div>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div
                  onMouseDown={(e) => e.stopPropagation()}
                  className="absolute right-0 mt-2 w-48 z-[999999] bg-white shadow-lg rounded-lg"
                >
                  <ul className="py-2">
                    <li className="px-4 py-2 hover:bg-gray-100">
                      <Link to="/book-appointment">Book Appointment</Link>
                    </li>
                    {isLoggedIn ? (
                      <li
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={logoutHandler}
                      >
                        Logout
                      </li>
                    ) : (
                      <li className="px-4 py-2 hover:bg-gray-100">
                        <Link to="/login">Login</Link>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Secondary Navigation - adjusted col-start to 4 */}
          <div className="col-span-7 col-start-4 row-start-2 flex w-fit ml-auto pr-8 bg-white">
            <div className="text-primary text-sm font-medium  font-poppins flex items-center gap-8 border-b">
              {additionalItems.map(({ text, pathname }) => (
                <NavItem
                  key={text}
                  text={text}
                  pathname={pathname}
                  icon="+"
                  isActive={activeMenu === text}
                />
              ))}
            </div>
          </div>

          {/* Main Navigation - adjusted col-start to 4 */}
          <div
            className="col-span-7 col-start-4 row-start-3 flex items-center justify-between px-20 bg-white"
            onMouseLeave={handleMouseLeave}
          >
            {navItems.map(({ src, alt, text, icon, pathname }) => {
              if (text === "Treatment") {
                return (
                  <div
                    key={text}
                    className="relative"
                    onMouseEnter={handleMouseEnter}
                  >
                    <div className="cursor-pointer">
                      <NavItem
                        src={src}
                        alt={alt}
                        text={text}
                        icon={icon}
                        pathname={pathname}
                        isActive={activeMenu === text || isSpecialtiesOpen}
                      />
                    </div>
                    <SpecialtiesDropdown
                      isOpen={isSpecialtiesOpen}
                      setIsOpen={setIsSpecialtiesOpen}
                    />
                  </div>
                );
              }
              return (
                <NavItem
                  key={text}
                  src={src}
                  alt={alt}
                  text={text}
                  icon={icon}
                  pathname={pathname}
                  isActive={activeMenu === text}
                />
              );
            })}
          </div>

          {/* Book Appointment Button - adjusted col-start to 11 */}
          <div
            onClick={navigateBookPage}
            className="relative cursor-pointer row-span-2 col-start-11 row-start-2 bg-primary font-poppins font-semibold text-white flex justify-center items-center text-center 2xl:text-sm text-xs border-2 border-transparent hover:border-white hover:shadow-[0_0_10px_2px_rgba(255,255,255,0.8)] transition-all duration-200 animate-[pulse_3s_ease-in-out_infinite] z-[50]"
          >
            Book <br /> Appointment
            <img
              onClick={navigateBookPage}
              src={ic_date}
              alt="ic_date"
              className="absolute left-0 translate-x-[-50%] hover:rotate-6 transition-transform duration-300"
            />
          </div>
        </div>
      </header>

      {/* Mobile Nav - Show on screens 1024px and below */}
      <nav className="xl:hidden block font-PoppinsRegular fixed top-0 left-0 right-0 bg-white z-[1001] border-b border-[#D5D5D5]">
        <div className="flex justify-between items-center gap-3 py-2 px-5">
          <img
            src={ic_menu}
            alt="ic_menu"
            width={35}
            height={35}
            onClick={() => setIsNavOpen(!isNavOpen)}
          />
          <Link to={"/"}>
            <img
              src={pp_savani}
              alt="pp_savani"
              width={120}
              height={120}
              className="2xl:w-80 md:w-72 h-20 w-44"
            />
          </Link>
          <ul className="flex items-center justify-end gap-2">
            <li className="relative" ref={ambulanceRef}>
              <img
                src={ic_ambulance}
                alt="ic_ambulance"
                className="cursor-pointer w-8 h-20"
                onClick={toggleAmbulanceDropdown}
              />

              {/* Dropdown for Ambulance Number */}
              <div
                className={`absolute right-0 mt-2 w-40 bg-white shadow-lg rounded-lg transition-all transform origin-top ${
                  isAmbulanceDropdownOpen
                    ? "opacity-100 scale-y-100"
                    : "opacity-0 scale-y-0"
                }`}
                style={{ transitionDuration: "300ms" }}
              >
                <div className="p-3 text-center">
                  <p className="text-lg font-bold text-primary">Ambulance</p>
                  <p className="text-xl font-semibold text-gray-700">
                    9925077200
                  </p>
                  <a
                    href="tel:9925077200"
                    style={{ display: "flex" }}
                    className="mt-2 text-white bg-primary px-4 py-2 rounded-md items-center justify-center gap-2"
                  >
                      <FaPhoneAlt /> Call Now
                  
                  </a>
                </div>
              </div>
            </li>
            <li>
              <div className="relative" ref={dropdownRef}>
                <div onClick={toggleDropdown} className="cursor-pointer">
                  <FaUserCircle
                    className="text-primary login_icon "
                    title="Login Icon"
                  />
                </div>

                {isDropdownOpen && (
                  <div
                    onMouseDown={(e) => e.stopPropagation()}
                    className="absolute right-0 mt-2 w-48 z-50 bg-white shadow-lg rounded-lg"
                  >
                    <ul className="py-2">
                      <li className="px-4 py-2 hover:bg-gray-100">
                        <Link to="/book-appointment">Book Appointment</Link>
                      </li>
                      {isLoggedIn ? (
                        <li
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                          onClick={logoutHandler}
                        >
                          Logout
                        </li>
                      ) : (
                        <li className="px-4 py-2 hover:bg-gray-100">
                          <Link to="/login">Login</Link>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </li>
          </ul>
        </div>
        <div
          ref={overlayRef}
          className={cx(
            isNavOpen ? "fixed inset-0 bg-black bg-opacity-50" : "hidden",
            "xl:hidden"
          )}
          onClick={() => setIsNavOpen(false)}
        />
        <div
          ref={sidebarRef}
          className={cx(
            isNavOpen
              ? "transform translate-x-0"
              : "transform -translate-x-full",
            "xl:hidden fixed top-0 left-0 z-50 w-64 bg-white h-full transition-transform duration-300 ease-in-out px-4 py-2 border-r border-primary shadow-lg overflow-y-scroll"
          )}
        >
          <article className="bg-white w-full h-fit rounded-3xl flex justify-center sm:gap-3 gap-2 items-center">
            <img
              src={pp_savani}
              alt="pp_savani"
              width={120}
              height={120}
              className="2xl:w-80 h-20 w-52"
            />
          </article>
          <div className="flex flex-col items-start mt-2">
            {navItems.map(({ src, alt, text, icon, pathname }) => (
              <div key={text} className="border-b py-2 w-full">
                {text === "Treatment" ? (
                  <>
                    <div
                      className={`flex justify-between items-center cursor-pointer ${
                        activeMenu === text
                          ? "text-secondary font-bold"
                          : "text-primary font-semibold"
                      }`}
                      onClick={toggleMobileTreatment}
                    >
                      <span>{text}</span>
                      <span className="ml-2">
                        {mobileTreatmentOpen ? "-" : "+"}
                      </span>
                    </div>
                    {mobileTreatmentOpen && (
                      <div className="mt-2 ml-4 max-h-[60vh] overflow-y-auto">
                        <SpecialtiesDropdown
                          isOpen={true}
                          setIsOpen={setIsSpecialtiesOpen}
                          mobileView={true}
                          closeSidebar={() => setIsNavOpen(false)}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={pathname}
                    onClick={() => setIsNavOpen(false)}
                    className={`block ${
                      activeMenu === text
                        ? "text-secondary font-bold"
                        : "text-primary font-semibold"
                    }`}
                  >
                    {text}
                  </Link>
                )}
              </div>
            ))}
          </div>
          <hr className="my-3 border-secondary" />
          <div className="flex flex-col items-start mt-3">
            {additionalItems.map(({ text, pathname }) => (
              <div key={text} className="border-b py-2 w-full">
                <Link
                  to={pathname}
                  onClick={() => setIsNavOpen(false)}
                  className={`block ${
                    activeMenu === text
                      ? "text-secondary font-bold"
                      : "text-primary font-semibold"
                  }`}
                >
                  {text} <span className="text-secondary">+</span>
                </Link>
              </div>
            ))}
          </div>
          <hr className="my-3 border-secondary" />
          <div className="text-primary text-sm font-medium flex flex-col items-start">
            <Link
              onClick={() => setIsNavOpen(false)}
              to="/patient-process"
              className={`2xl:text-sm text-xs font-semibold border-b py-2 w-full ${
                activeMenu === "Patients Process"
                  ? "text-secondary"
                  : "text-primary"
              }`}
            >
              Patients Process <span className="text-secondary">+</span>
            </Link>
            <Link
              onClick={() => setIsNavOpen(false)}
              to="/visitors"
              className={`2xl:text-sm text-xs font-semibold border-b py-2 w-full ${
                activeMenu === "Visitor Guidelines"
                  ? "text-secondary"
                  : "text-primary"
              }`}
            >
              Visitor Guidelines <span className="text-secondary">+</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Spacer for fixed navbar - reduced height */}
      <div className="xl:h-[105px] h-[60px]"></div>

      {/* Main Content */}
      <main className="relative">{/* Your page content */}</main>
    </>
  );
}

export default NavBar;
